<template>
  <div class="prescription-componenet-container">
    <alert
      class="mt-5 mx-4 shadow-card rounded"
      color="secondary"
      v-if="
        authedUser.isPatient &&
        !loading &&
        !(originalPrescription || {}).signed_at
      "
      >Access denied – prescription has not been signed yet. Please check back
      later or contact your
      {{
        this.$providerLabel
          ? this.$providerLabel({ type: "singular", capitalize: false })
          : "provider"
      }}
      for updates.</alert
    >
    <div
      class="prescription-container p-3 m-3"
      v-if="
        !loading &&
        (!authedUser.isPatient || (originalPrescription || {}).signed_at)
      "
      ref="pdfPrescriptionContainer"
    >
      <div
        class="d-flex justify-content-end"
        v-if="!(originalPrescription || {}).signed_at && !hideControls"
      >
        <button class="btn btn-sm btn-theme" @click="changeEditMode">
          <i class="fas mr-2" :class="editMode ? 'fa-eye' : 'fa-edit'"></i>
          {{ editMode ? "Preview" : "Edit" }}
        </button>
      </div>
      <div class="prescription-inner-container">
        <div class="prescription-header mt-3">
          <h2 class="text-center" v-if="!editMode">
            {{ editingPrescription.clinic_name }}
          </h2>
          <div class="edit_clinic_container" v-else>
            <div class="col-12 col-sm-8 col-md-7 mx-auto mb-3">
              <select-control
                v-model="selectedClinic"
                :options="[
                  {
                    label: `Select ${$clinicLabel({
                      type: 'singular',
                      capitalize: true,
                    })}`,
                    value: '',
                  },
                  ...patientClinics,
                ]"
                type="vertical"
                wrapClass="mb-0"
                inputClass="light-shadow"
                @input="changeClinicName"
              >
              </select-control>

              <div class="or-divider">OR</div>

              <input-control
                type="horizontal"
                wrapClass="m-0"
                v-model="editingPrescription.clinic_name"
                inputClass="p-0"
                control="text"
                labelClass="d-none"
                :placeholder="`Enter ${$clinicLabel({
                  type: 'singular',
                  capitalize: false,
                })} name manually`"
                >{{
                  $clinicLabel({ type: "singular", capitalize: true })
                }}</input-control
              >
            </div>
          </div>
        </div>
        <div class="client-info">
          <table class="table client-info-table my-2">
            <colgroup>
              <col style="width: 60%" />
              <col style="width: 40%" />
            </colgroup>
            <tbody>
              <tr>
                <td class="py-1 border-0">
                  <b>{{ resolvedPatient.name }}</b>
                </td>
                <td class="py-1 border-0">
                  <b>Gender:</b>
                  {{
                    resolvedPatient.gender == "1"
                      ? "Male"
                      : resolvedPatient.gender == "2"
                      ? "Female"
                      : "Other"
                  }}
                </td>
              </tr>
              <tr>
                <td class="py-1 border-0">
                  <b>DOB:</b>
                  {{
                    prettyDate(resolvedPatient.user?.birth_date, "MMM DD, YYYY")
                  }}
                </td>
                <td class="py-1 border-0">
                  <b>Email: </b>{{ resolvedPatient.email }}
                </td>
              </tr>
              <tr>
                <td class="py-1 border-0">
                  <b>Phone:</b> {{ resolvedPatient.phone_number }}
                </td>
                <td class="py-1 border-0"></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          class="prescription_notes"
          v-if="editMode || editingPrescription.notes"
        >
          <input-control
            type="horizontal"
            v-if="editMode"
            v-model="editingPrescription.notes"
            control="textarea"
            wrapClass="col-12 my-3 p-0 mx-0"
            inputClass="p-0"
            labelClass="d-none"
            placeholder="Enter notes here..."
            >Notes</input-control
          >
          <div class="prescription-notes-text" v-else>
            <p class="my-3 border rounded p-2">
              {{ editingPrescription.notes }}
            </p>
          </div>
        </div>

        <div class="medications mt-3">
          <h2 class="mb-2">Medications</h2>
          <table
            class="table medication-table mb-0"
            :class="{ 'table-bordered': editMode }"
            v-if="
              editMode ||
              editingPrescription.medications[0]?.title ||
              editingPrescription.medications[0]?.description
            "
          >
            <colgroup>
              <col style="width: 50%" />
              <col style="width: 50%" />
            </colgroup>
            <thead v-if="editMode">
              <tr>
                <td class="py-2 font-weight-bold">Medication</td>
                <td class="py-2 font-weight-bold">Frequency</td>
                <td class="px-2 py-1 text-center" v-if="editMode">
                  <i class="fas fa-prescription mt-2"></i>
                </td>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(medication, index) in editingPrescription.medications"
                :key="index"
              >
                <td class="px-0 py-2">
                  <input-control
                    type="horizontal"
                    v-model="medication.title"
                    control="text"
                    wrapClass="m-0"
                    labelClass="d-none"
                    inputClass="py-0 px-2"
                    placeholder="e.g., Amoxicillin"
                    v-if="editMode"
                    >title</input-control
                  >
                  <span class="font-weight-bold px-2" v-else>{{
                    medication.title
                  }}</span>
                </td>
                <td class="px-0 py-2">
                  <input-control
                    type="horizontal"
                    wrapClass="m-0"
                    v-model="medication.description"
                    inputClass="py-0 px-2"
                    control="text"
                    labelClass="d-none"
                    placeholder="e.g., 1 tablet daily"
                    v-if="editMode"
                    >DESCRIPTION</input-control
                  >
                  <p class="px-2 text-right mb-0" v-else>
                    {{ medication.description }}
                  </p>
                </td>
                <td v-if="editMode" class="text-center px-2 py-0">
                  <button
                    class="btn btn-link text-danger p-0"
                    @click="removeMedication(index)"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <button
            class="btn btn-link mt-2 py-2"
            @click="addMedication"
            v-if="editMode"
          >
            <i class="fa fa-plus"></i> Add More
          </button>
        </div>
        <div
          class="
            signature-container
            mt-5
            d-flex
            justify-content-end
            col-12 col-md-6
            offset-md-6
          "
        >
          <div
            class="signature-box w-100 text-right"
            v-if="editingPrescription.signature"
          >
            <img
              :src="editingPrescription.signature"
              alt="Signature"
              class="img-fluid"
            />
            <hr class="separator" />
            <p class="mb-0 font-weight-bold">
              {{ editingPrescription.signed_by?.name || authedUser.name }}
              <span class="small" v-if="editingPrescription.college_number"
                >({{ editingPrescription.college_number }})</span
              >
            </p>
            <p
              class="mb-0 mt-4 text-muted small"
              v-if="editingPrescription.signed_at"
            >
              Produced on: {{ prettyDate(editingPrescription.signed_at) }}
            </p>
          </div>
        </div>
      </div>
      <div class="col-12 col-sm-8 col-md-7 mt-3 px-0">
        <select-control
          v-if="
            editMode &&
            !(originalPrescription || {}).signed_at &&
            editingPrescription.signature
          "
          v-model="selectedPharmacy"
          :options="[
            { label: 'Select Pharmacy', value: '' },
            ...patientPharmacies,
          ]"
          type="vertical"
          wrapClass="mb-0"
          inputClass="light-shadow"
        >
        </select-control>
      </div>
      <div
        class="custom-control custom-switch mt-4 mx-2"
        v-if="
          editMode &&
          !(originalPrescription || {}).signed_at &&
          editingPrescription.signature
        "
      >
        <input
          type="checkbox"
          class="custom-control-input"
          id="rescriptionEmail"
          v-model="sendRescriptionToMe"
        />
        <label class="custom-control-label" for="rescriptionEmail"
          >Send me a copy of this prescription</label
        >
      </div>
    </div>
    <div
      class="actions border-top mt-5 py-3 d-flex justify-content-end"
      v-if="!loading"
    >
      <div v-if="!hideControls">
        <button
          class="btn btn-deep-success"
          @click="savePrescription()"
          v-if="!(originalPrescription || {}).signed_at"
        >
          <i class="fas fa-circle-notch fa-spin mr-1" v-if="saving"></i>
          Save
          {{
            editingPrescription.signature && !editingPrescription.signed_at
              ? " & Sign"
              : "Changes"
          }}
        </button>
        <button
          class="btn btn-black mx-2"
          v-if="!editingPrescription.signature"
          @click="addSignature()"
          :disabled="!editMode"
        >
          Add Signature
        </button>

        <button
          class="btn btn-deep-red mx-2"
          v-if="canBeRemoved"
          @click="removePrescription()"
        >
          <i class="fas fa-circle-notch fa-spin mr-1" v-if="removing"></i>
          Delete
        </button>
      </div>
      <button
        class="btn btn-deep-red mr-3"
        @click="downloadPrescriptionPDF()"
        v-if="(originalPrescription || {}).signed_at"
      >
        <i class="fas fa-lg fa-file-pdf mr-1" v-if="!downloading"></i>
        <i class="fas fa-circle-notch fa-spin mr-2" v-else></i>Save PDF
      </button>
      <button class="btn btn-link" @click="cancel()">
        {{
          hideControls || (originalPrescription || {}).signed_at
            ? "Close"
            : "Cancel"
        }}
      </button>
    </div>
    <div v-if="loading">
      <alert class="mt-5 mx-4 shadow-card" color="secondary" v-if="loading" />
    </div>
  </div>
</template>

<script>
import dateFormat from "@/utils/dateFormat";
import html2pdf from "html2pdf.js";
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";

export default {
  name: "PrescriptionItem",
  props: ["prescription", "prescriptionId", "engagementId", "hideControls"],
  data() {
    return {
      loading: false,
      saving: false,
      removing: false,
      downloading: false,
      editMode: false,
      sendRescriptionToMe: false,
      selectedPharmacy: "",
      selectedClinic: "",
      originalPrescription: this.prescription || null,
      editingPrescription: {
        notes: "",
        medications: [],
        signature: null,
      },
    };
  },
  computed: {
    ...mapState({
      authedUser: (state) => state.auth.user || {},
      patient: (state) => state.patients.patient,
    }),
    resolvedPatient() {
      return this.editingPrescription?.patient || this.patient;
    },
    patientPharmacies() {
      if (this.resolvedPatient) {
        return (this.resolvedPatient.pharmacies || []).map((item) => ({
          value: item.id,
          label: item.name,
          email: item.email,
        }));
      }
      return [];
    },
    selectedPharmacyHasEmail() {
      if (!this.selectedPharmacy) return false;
      const pharmacy = this.patientPharmacies.find(
        (item) => item.value === Number(this.selectedPharmacy)
      );
      return pharmacy && pharmacy.email;
    },
    patientClinics() {
      if (this.resolvedPatient) {
        return (this.resolvedPatient.user.clinics || []).map((item) => ({
          value: item.id,
          label: item.name,
        }));
      }
      return [];
    },
    canBeRemoved() {
      return (
        this.editingPrescription.id &&
        !this.editingPrescription.signed_at &&
        (this.authedUser.isAdmin ||
          this.authedUser.isManagingAdmin ||
          this.authedUser.id == this.editingPrescription.created_by.id)
      );
    },
    checkEmailPharmacyExists(id) {
      return this.patientPharmacies.some((pharmacy) => pharmacy.value === id);
    },
  },
  mounted() {
    if (this.prescription) {
      this.originalPrescription = this.prescription;
      this.editingPrescription = this.prescription;
      this.selectedClinic =
        (
          this.patientClinics.filter(
            (item) => item.label == this.editingPrescription.clinic_name
          )[0] || {}
        ).value || "";
    } else if (this.prescriptionId) {
      this.loading = true;
      this.getPrescription(this.prescriptionId).then((prescription) => {
        this.editingPrescription = prescription;
        this.originalPrescription = prescription;
        this.selectedClinic =
          (
            this.patientClinics.filter(
              (item) => item.label == this.editingPrescription.clinic_name
            )[0] || {}
          ).value || "";
        this.editMode = false;
        this.loading = false;
      });
    } else {
      this.editMode = true;
      this.editingPrescription.notes = "";
      this.editingPrescription.medications = [{ title: "", description: "" }];
    }
  },
  methods: {
    ...mapActions({
      getPrescription: "patients/getPrescription",
      addPrescription: "patients/addPrescription",
      updatePrescription: "patients/updatePrescription",
      deletePrescription: "patients/deletePrescription",
    }),
    dateFormat: dateFormat,
    prettyDate: (date, format = null) => {
      if (date == undefined || date == null) {
        return;
      }
      if (!format) {
        return dateFormat(date, "human");
      } else {
        return dateFormat(date, format);
      }
    },
    addMedication: function () {
      this.editingPrescription.medications.push({
        title: "",
        description: "",
      });
    },
    removeMedication: function (index) {
      this.editingPrescription.medications.splice(index, 1);
    },
    addSignature: function () {
      if (!this.authedUser || !this.authedUser.signature) {
        Swal.fire({
          title: "Signature Required",
          text: "Please add your signature to your profile to proceed.",
          icon: "warning",
        });
      } else {
        this.editingPrescription.signature = this.authedUser.signature;
      }
    },
    changeClinicName(value) {
      this.editingPrescription.clinic_name = this.patientClinics.filter(
        (item) => item.value == value
      )[0].label;
    },
    changeEditMode: function () {
      this.editMode = !this.editMode;
    },
    cancel: function () {
      this.editingPrescription = {
        notes: "",
        medications: [{ title: "", description: "" }],
        clinic_name: "",
        signature: null,
      };
      if (this.originalPrescription) {
        this.editingPrescription = this.originalPrescription;
        this.editMode = false;
      }
      this.$emit("cancel");
    },
    savePrescription: function () {
      const vm = this;
      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          confirmButton: "btn btn-black ml-4",
          cancelButton: "btn btn-danger",
        },
        buttonsStyling: false,
      });

      if (
        this.editingPrescription.signature &&
        !this.editingPrescription.signed_at
      ) {
        if (!this.selectedPharmacy) {
          return Swal.fire({
            title: "Pharmacy Required",
            text: "Please select a pharmacy to send the prescription.",
            icon: "warning",
          });
        }
        if (!this.selectedPharmacyHasEmail) {
          return swalWithBootstrapButtons
            .fire({
              title: "Pharmacy has no Email",
              html: "The selected pharmacy has no email address. The prescription will be sent to your email instead.",
              icon: "warning",
              showCancelButton: true,
              confirmButtonText: "Proceed",
              cancelButtonText: "Cancel",
              reverseButtons: true,
            })
            .then((result) => {
              if (result.isConfirmed) {
                vm.sendRescriptionToMe = true;
                vm.showSignConfirmation(swalWithBootstrapButtons);
              }
            });
        }
        this.showSignConfirmation(swalWithBootstrapButtons);
      } else {
        this.savePrescriptionHelper();
      }
    },
    showSignConfirmation: function (swalWithBootstrapButtons) {
      const vm = this;
      swalWithBootstrapButtons
        .fire({
          title: "Are you sure?",
          html: "Since you are signing this prescription, it will be considered final and cannot be edited further.<br><br> The prescription will be sent to the selected pharmacy and/or you via email",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, sign it!",
          cancelButtonText: "No, cancel!",
          reverseButtons: true,
        })
        .then((result) => {
          if (result.isConfirmed) {
            vm.savePrescriptionHelper();
          }
        });
    },
    savePrescriptionHelper: function () {
      let data = JSON.parse(JSON.stringify(this.editingPrescription));

      if (!data.clinic_name) {
        return Swal.fire({
          title: `${this.$clinicLabel({
            type: "singular",
            capitalize: true,
          })} Name Required`,
          text: `Please enter a ${this.$clinicLabel({
            type: "singular",
            capitalize: false,
          })} name.`,
          icon: "error",
        });
      }
      if (
        data.medications.filter(
          (item) =>
            (item.title && !item.description) ||
            (!item.title && item.description)
        ).length
      ) {
        return Swal.fire({
          title: "Incorrect Medications",
          text: "Please add valid medications with both medication and frequency.",
          icon: "error",
        });
      }
      data.medications = data.medications.filter(
        (item) => item.title && item.description
      );
      this.saving = true;
      if (this.editingPrescription.id) {
        this.updatePrescription({
          ...data,
          pharmacy_id: this.selectedPharmacy,
          send_me: this.sendRescriptionToMe,
        }).then((success) => {
          this.saving = false;
          if (success) {
            Swal.fire({
              title: "Prescription Updated",
              text: "The prescription has been updated successfully.",
              icon: "success",
            });
            this.editMode = false;
            this.$emit("save", this.editingPrescription);
          }
        });
      } else {
        this.addPrescription({
          note_id: this.engagementId,
          ...data,
          pharmacy_id: this.selectedPharmacy,
          send_me: this.sendRescriptionToMe,
        }).then((success) => {
          this.saving = false;
          if (success) {
            Swal.fire({
              title: "Prescription Created",
              text: "The prescription has been created successfully.",
              icon: "success",
            });
            this.$emit("save", this.editingPrescription);
          }
        });
      }
    },
    removePrescription: function () {
      const vm = this;
      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          confirmButton: "btn btn-black ml-4",
          cancelButton: "btn btn-danger",
        },
        buttonsStyling: false,
      });

      swalWithBootstrapButtons
        .fire({
          title: "Are you sure?",
          html: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, delete it!",
          cancelButtonText: "No, cancel!",
          reverseButtons: true,
        })
        .then((result) => {
          if (result.isConfirmed) {
            vm.removing = true;
            vm.deletePrescription(this.editingPrescription.id).then(
              (success) => {
                vm.removing = false;
                if (success) {
                  this.$emit("delete", this.editingPrescription);
                }
              }
            );
          }
        });
    },
    downloadPrescriptionPDF: function () {
      this.downloading = true;
      setTimeout(() => {
        html2pdf(this.$refs.pdfPrescriptionContainer, {
          margin: 0,
          filename: `prescription_${this.editingPrescription?.id || "0"}.pdf`,
          image: { type: "jpeg", quality: 1 },
          html2canvas: {
            scale: 4,
            allowTaint: true,
            useCORS: true,
            letterRendering: true,
            width: 800,
          },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
          .then(() => {
            this.downloading = false;
          })
          .catch(() => {
            this.downloading = false;
            Swal.fire({
              title: "Error",
              html: "Something went wrong...",
              icon: "error",
            });
          });
      }, 100);
    },
  },
};
</script>

<style lang="scss">
.prescription-container {
  border: 4px solid #686767;
  border-radius: 2px;

  .prescription-header,
  .client-info,
  .prescription_notes {
    border-bottom: 2px solid #686767;
  }
  .medication-table {
    input {
      border-radius: 4px;
    }
  }
  .signature-container {
    .signature-box {
      .separator {
        border-top: 2px solid #686767;
      }
    }
  }
}
</style>
